import ClearIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import type { FormikValues } from "formik";
import { useField, useFormikContext } from "formik";
import { get as _get } from "lodash";
import type { ReactNode } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { TruncatedText } from "@citrine/atoms/truncated-text";
import type { DataFullstoryId } from "@citrine/types/full-story";

import type { FormProps } from "./form";
import { Form, useFormConfiguration } from "./form";
import * as sx from "./integrated-text-field.sx";
import type { SelectProps } from "./select";

type IntegratedSelectProps<
  Shape extends FormikValues,
  ValueType extends string | undefined = string,
> = Omit<SelectProps<Shape, ValueType>, "classes"> & {
  "data-testid"?: string;
  disableConfirm?: boolean;
  disableCancel?: boolean;
  editButtonFullstoryId?: DataFullstoryId;
  initializeAsEditing?: boolean;
  onCancel?: () => void;
  onEdit?: () => void;
  renderValue?: (value: string | number) => ReactNode;
};

export function IntegratedSelect<
  Shape extends FormikValues = FormikValues,
  ValueType extends string | undefined = string,
>({
  "aria-labelledby": ariaLabelledBy,
  className,
  "data-testid": dataTestId,
  disableConfirm,
  disableCancel,
  editButtonFullstoryId,
  initializeAsEditing,
  onCancel,
  onEdit,
  renderValue,
  size,
  ...passThroughProps
}: IntegratedSelectProps<Shape, ValueType>) {
  const { disabled, name, label } = passThroughProps;

  const { submitOnEnter } = useFormConfiguration();

  const { formatMessage } = useIntl();
  const [{ value }, { error }, { setValue }] = useField<string | number>(name);
  const { submitForm, validateForm } = useFormikContext();
  const [isEditing, setIsEditing] = useState(initializeAsEditing);
  const { readonly } = useFormConfiguration();

  const prevValue = useRef<string | number>(value);

  const cancel = useCallback(async () => {
    setIsEditing(false);
    setValue(prevValue.current);
    await onCancel?.();
  }, [onCancel, setValue]);

  const confirm = useCallback(async () => {
    /**
     * submitForm does not throw correctly for nested errors
     * so we need to gate on explicit validation
     */
    const errors = await validateForm();
    if (!_get(errors, name)) {
      if (submitOnEnter) {
        await submitForm();
      }
      setIsEditing(false);
    }
  }, [name, submitForm, submitOnEnter, validateForm]);

  const renderedValue = useMemo(
    () => renderValue?.(value) ?? value,
    [renderValue, value]
  );

  return (
    <sx.Container className={className} data-testid={dataTestId} size={size}>
      {isEditing ? (
        <>
          <sx.Select {...(passThroughProps as any)} name={name} size={size} />
          <sx.Actions active size={size}>
            {/* Cancel edit button. Resets value */}
            <sx.SquareButton
              key="cancel" // adding keys to prevent tooltips from hanging between states
              disabled={disableCancel}
              onClick={cancel}
              size={size}
              tooltip={formatMessage({
                id: "cancel",
              })}
            >
              <ClearIcon fontSize="small" />
            </sx.SquareButton>
            {/* Confirm edit button */}
            <sx.ConfirmButton
              key="confirm"
              data-testid="submit-button"
              disabled={disabled || disableCancel}
              onClick={confirm}
              size={size}
              tooltip={formatMessage({
                id: "Confirm",
              })}
              type="submit"
            >
              <CheckIcon fontSize="small" />
            </sx.ConfirmButton>
          </sx.Actions>
        </>
      ) : (
        <>
          <sx.ValueContainer hasError={Boolean(error)} size={size}>
            <sx.Value aria-labelledby={ariaLabelledBy} asPlaceholder={!value}>
              <TruncatedText>{renderedValue || label}</TruncatedText>
            </sx.Value>
            {error ? <sx.ErrorMessage>{error}</sx.ErrorMessage> : null}
          </sx.ValueContainer>
          {readonly ? null : (
            <sx.Actions size={size}>
              {/* Edit button */}
              <sx.IconButton
                data-testid={`edit-${name}-button`}
                data-fullstoryid={editButtonFullstoryId}
                key="edit"
                disabled={disabled}
                onClick={() => {
                  prevValue.current = value;
                  setIsEditing(true);
                  onEdit?.();
                }}
                size={size}
                tooltip={formatMessage({
                  id: "Edit",
                })}
              >
                <EditIcon fontSize="small" />
              </sx.IconButton>
            </sx.Actions>
          )}
        </>
      )}
    </sx.Container>
  );
}

IntegratedSelect.slots = {
  Container: sx.Container,
  ValueContainer: sx.ValueContainer,
};

export function IntegratedSelectForm<
  Shape extends FormikValues = FormikValues,
  ValueType extends string | undefined = string,
>({
  enableReinitialize,
  initialValues,
  readonly,
  onSubmit,
  validate,
  ...passThroughProps
}: Omit<IntegratedSelectProps<Shape, ValueType>, "onSubmit" | "validate"> &
  Pick<
    FormProps<Shape>,
    | "enableReinitialize"
    | "initialValues"
    | "readonly"
    | "onSubmit"
    | "validate"
  >) {
  return (
    <Form
      enableReinitialize={enableReinitialize}
      initialValues={initialValues}
      readonly={readonly}
      onSubmit={onSubmit}
      submitOnEnter
      validate={validate}
    >
      <IntegratedSelect
        {...(passThroughProps as IntegratedSelectProps<Shape, ValueType>)}
      />
    </Form>
  );
}
